import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Smile, Trash2, Flag, Clock } from "lucide-react";
import { Comment, ResponseBattleSide } from "@dripit/types";

import { Button, Separator } from "@/components/ui";
import { useTranslate } from "@/hooks";
import {
  formatCurrency,
  formatRecentTimestamp,
  getBattleSide,
  resolveImage,
  usdcToUsd,
} from "@/helpers";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import { RootState } from "@/store";
import { cn } from "@/helpers/tailwind";
import isEmpty from "lodash.isempty";
import { CommentUserPopover } from "./CommentUserPopover";
import isNil from "lodash.isnil";

export interface NestedComment extends Comment {
  replies?: Comment[];
}

interface CommentItemProps {
  battleSides: ResponseBattleSide[];
  comment: NestedComment;
  replyTextArea: React.ReactNode;
}

export const CommentItem: React.FC<CommentItemProps> = ({
  battleSides,
  comment,
  replyTextArea,
}) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const [isReplyOpen, setIsReplyOpen] = React.useState(false);

  return (
    <div className="flex justify-between gap-3">
      <img
        className="mr-2 size-12 rounded-lg"
        src={resolveImage(comment?.user?.avatar, "600x600")}
        alt={comment?.user?.username ?? "User avatar"}
      />
      {/* <CommentUserPopover
        trigger={
          <img
            className="mr-2 size-12 cursor-pointer rounded-lg"
            src={resolveImage(comment?.user?.avatar, "600x600")}
            alt={comment?.user?.username ?? "User avatar"}
          />
        }
      /> */}

      <div className="flex w-full flex-grow flex-col">
        <div className="mb-2 flex items-center justify-between gap-3">
          <p
            className="cursor-pointer font-geist text-base font-semibold text-white hover:underline"
            onClick={() => navigate(`/profile/${comment?.user?.wallet ?? ""}`)}>
            {comment.user.username}
          </p>
          {/* user's positions */}
          {!isEmpty(comment?.user?.positions) && (
            <div className="flex items-center gap-2">
              <p
                className={cn(
                  "flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs",
                  "bg-indigo-400/10 text-indigo-400"
                )}>
                <span>
                  {getBattleSide(
                    comment?.user?.positions?.[0]?.battle_side_id,
                    battleSides ?? []
                  )?.artist?.name ?? "Unknown Artist"}
                </span>
                <span>•</span>
                <span>
                  {formatCurrency(
                    usdcToUsd(
                      Number(comment?.user?.positions?.[0]?.amount ?? 0)
                    )
                  )}
                </span>
              </p>
            </div>
          )}
          {/* comment actions */}
          <div className="flex-grow" />
          {/* <DropdownMenu>
            <DropdownMenuTrigger>...</DropdownMenuTrigger>
            <DropdownMenuContent>
              {false && (
                <DropdownMenuItem>
                  <Trash2 />
                  {t("components.delete")}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem>
                <Flag />
                {t("components.report")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu> */}
        </div>
        <p className="text-xs text-gray-400">{comment.text}</p>
        {/* comment actions */}
        <div className="mt-3 flex space-x-4 text-xs text-gray-400">
          {!isNil(replyTextArea) && (
            <>
              <Button
                variant="ghost"
                className={cn(
                  "bg-transparent text-xs text-gray-400 hover:bg-transparent",
                  "!h-auto !p-0"
                )}
                onClick={() => setIsReplyOpen(!isReplyOpen)}>
                {t("components.reply")}
              </Button>
              <Separator orientation="vertical" />
            </>
          )}
          <div className="flex space-x-2">
            <Clock width={15} height={15} />
            <span>
              {formatRecentTimestamp(Number(comment?.created_at ?? 0) * 1000)}
            </span>
          </div>
        </div>
        {/* reply text area */}
        <div
          className={cn(
            "mt-3 max-h-0 overflow-hidden",
            "transition-all duration-500 ease-in-out",
            {
              "max-h-[100px] overflow-y-auto": isReplyOpen,
            }
          )}>
          {replyTextArea}
        </div>
        {/* replies */}
        <div
          className={cn("flex flex-col gap-5", {
            "mt-5": !isEmpty(comment?.replies),
          })}>
          {comment.replies?.map((reply) => (
            <CommentItem
              key={reply.id}
              battleSides={battleSides}
              comment={reply}
              replyTextArea={replyTextArea}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
